/**
 * The two words this page turns a run's charge state into, and nothing else.
 *
 * `mockups/studio/money.html` tints a run row red for a charge that did not go
 * through and leaves everything else plain — CONTRACT.md §2.9: "Do **not** tint a
 * table where every row failed."
 *
 * The ledger fold's own tint used to be here too, as a byte-for-byte copy of
 * `components/client/InvoiceTable.tsx` `rowVariant`. That function now takes
 * `{ status }` rather than a whole `InvoiceView`, which is all it ever read, so the
 * copy is gone and there is one answer to "which invoices still want something".
 *
 * Both are written as the states that still want something, never as a list of ones
 * to ignore (`CLAUDE.md` §Gotchas: "`PAYMENT_STATUSES` gets longer"). `INVOICE_STATUSES`
 * has five today and `chargeStateOf` in `readmodels/studio-money.ts` composes its own
 * string from three of them plus an attempt count.
 */
import type { ExpenseKind } from '@fareast/shared'

/**
 * What the money-out ledger calls each kind of expense, in the two registers
 * `mockups/studio/money.html` uses.
 *
 * **Both are the mockup's own** and they are deliberately different: the answer
 * sentence counts things — "3 contractors, 8 subscriptions, 2 rail fees" — and the
 * section note lists what is present — "contractors, tooling, rails, draw". A
 * subscription is a subscription when you are counting them and tooling when you are
 * naming the category.
 *
 * One table rather than two, and a `Record<ExpenseKind, …>` rather than a
 * `Record<string, …>` with a fallback: the two lived in two files at opposite ends of
 * one screen, and each would have printed a sixth kind's raw code — `rail_fee` — with
 * nothing to notice. Typed against the union, a sixth kind is a compile error twice.
 */
export const EXPENSE_KIND_WORDS: Readonly<
  Record<ExpenseKind, { readonly one: string; readonly other: string; readonly short: string }>
> = {
  contractor: { one: 'contractor', other: 'contractors', short: 'contractors' },
  subscription: { one: 'subscription', other: 'subscriptions', short: 'tooling' },
  rail_fee: { one: 'rail fee', other: 'rail fees', short: 'rails' },
  draw: { one: 'draw', other: 'draws', short: 'draw' },
  other: { one: 'other line', other: 'other lines', short: 'other' },
}

/**
 * How a run row is tinted, from `MoneyRunRowSchema.charge_state`.
 *
 * That field is a **sentence** rather than a status — `readmodels/studio-money.ts`
 * `chargeStateOf` answers `settled`, `declined ×3`, `processing`, or the invoice's own
 * word — so this reads its first word. A run row with no charge state at all is a run
 * that has not reached the step, which is not a failure.
 */
export function chargeVariant(chargeState: string | null): 'need' | 'bad' | undefined {
  if (chargeState === null) return undefined
  if (chargeState.startsWith('declined') || chargeState === 'failed') return 'bad'
  if (chargeState === 'processing' || chargeState === 'sent') return 'need'
  return undefined
}
